/**
 * App update gate — version check.
 *
 * Reads the TRUE installed native version (Android `versionName` / iOS
 * `CFBundleShortVersionString`) via expo-application and compares it to the
 * server's per-platform minimum / recommended versions.
 *
 * Fail-open by design: any error, timeout, missing config, or unknown installed
 * version resolves to `'ok'` so the gate never blocks the app on its own failure.
 *
 * Uses a bare axios call (NOT useApiClient) because the gate runs pre-auth and
 * the authenticated client rejects requests when signed out.
 */

import axios from 'axios';
import { Platform } from 'react-native';
import * as Application from 'expo-application';
import { API_URL, API_ENDPOINTS } from '../../../constants/api';
import { logger } from '../../helpers/logger.helper';
import { deriveUpdateStatus } from './appUpdateStatus.helper';
import type {
  UpdateStatus,
  AppPlatform,
  PlatformVersionConfig,
} from './appUpdateStatus.helper';

export type { UpdateStatus, AppPlatform, PlatformVersionConfig };

export interface AppVersionCheckResult {
  status: UpdateStatus;
  platform: AppPlatform;
  /** Installed native version, or null when unknown (dev / web). */
  currentVersion: string | null;
  recommendedVersion: string | null;
  message: string | null;
}

const REQUEST_TIMEOUT_MS = 6000;

const versionClient = axios.create({
  baseURL: API_URL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
});

const okResult = (
  platform: AppPlatform,
  currentVersion: string | null,
): AppVersionCheckResult => ({
  status: 'ok',
  platform,
  currentVersion,
  recommendedVersion: null,
  message: null,
});

/**
 * Determine whether the installed app must / should update.
 * Always resolves (never throws) — failures map to `'ok'`.
 */
export async function checkAppVersion(): Promise<AppVersionCheckResult> {
  const platform = Platform.OS as AppPlatform;

  // Web always reloads to the latest deploy — there is no store version to gate.
  if (platform === 'web') {
    return okResult('web', null);
  }

  const currentVersion = Application.nativeApplicationVersion;
  // Unknown installed version (e.g. Expo Go / dev) — fail open.
  if (!currentVersion) {
    return okResult(platform, null);
  }

  try {
    const response = await versionClient.get<{
      success: boolean;
      data?: Record<string, PlatformVersionConfig>;
    }>(API_ENDPOINTS.appConfig.version);

    const config = response.data?.data?.[platform];
    const status = deriveUpdateStatus(currentVersion, config);

    if (status === 'ok') {
      return okResult(platform, currentVersion);
    }

    return {
      status,
      platform,
      currentVersion,
      recommendedVersion: config?.recommendedVersion ?? null,
      message: config?.message ?? null,
    };
  } catch (error) {
    logger.warn('[appVersionCheck] check failed, failing open', {
      message: error instanceof Error ? error.message : String(error),
    });
    return okResult(platform, currentVersion);
  }
}
